/** One field of the client order: label, the right input for its kind, where a prefilled value came from. */
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { NumberField } from '@/cowork/shared/NumberField';
import type { ClientField, FieldSource, Value } from './api';
import { SOURCE_LABEL, optionLabel } from './model';

const box = 'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[14px] text-[#101828] placeholder:text-[#98a2b3] transition-all focus:border-[#be43ff] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/25 disabled:bg-[#f9fafb] disabled:text-[#667085]';

export function SourceTag({ source }: { source?: FieldSource }) {
  const label = source ? SOURCE_LABEL[source] : undefined;
  if (!label) return null;
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#f4ebff] px-2 py-0.5 text-[11px] text-[#6941c6]" style={{ fontWeight: 500 }}><Sparkles className="h-3 w-3" aria-hidden />{label}</span>;
}

export function FieldRow({ field, value, source, missing, disabled, products, onSave }: {
  field: ClientField; value: Value | undefined; source?: FieldSource; missing?: boolean; disabled?: boolean; products?: string[];
  onSave: (v: Value) => void;
}) {
  const id = `ck-field-${field.id}`;
  return (
    <div id={id} className={cn('scroll-mt-28 flex flex-col gap-1.5 rounded-xl', missing && 'ring-2 ring-[#fda29b] ring-offset-4 ring-offset-white')}>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${id}-input`} className="text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>{field.label}{field.required && <span className="sr-only"> (required)</span>}</label>
        <SourceTag source={source} />
      </div>
      <Input field={field} value={value} disabled={disabled} onSave={onSave} inputId={`${id}-input`} products={products} />
      {field.hint && <span className="text-[12px] text-[#667085]">{field.hint}</span>}
      {missing && <span className="text-[12px] text-[#b42318]">Needed to order</span>}
    </div>
  );
}

function Input({ field, value, disabled, onSave, inputId, products }: { field: ClientField; value: Value | undefined; disabled?: boolean; onSave: (v: Value) => void; inputId: string; products?: string[] }) {
  const str = Array.isArray(value) ? value.join(', ') : value ?? '';
  const list = Array.isArray(value) ? value : value ? [value] : [];
  switch (field.kind) {
    case 'long': return <TextBox id={inputId} area value={str} placeholder={field.placeholder} disabled={disabled} onCommit={onSave} />;
    case 'number': return <div className="max-w-[160px]"><NumberField id={inputId} value={str ? Number(str) : null} onChange={(n) => onSave(n == null ? '' : String(n))} commit="blur" allowEmpty min={0} disabled={disabled} className={box} /></div>;
    case 'date': return <input id={inputId} type="date" value={str} disabled={disabled} onChange={(e) => onSave(e.target.value)} className={cn(box, 'max-w-[200px]')} />;
    case 'select': {
      const opts = field.options || [];
      if (opts.length <= 7) return <Choice options={opts.map((o) => ({ id: o, label: optionLabel(field, o) }))} value={str} disabled={disabled} label={field.label} onChange={(v) => onSave(v)} />;
      return (
        <select id={inputId} value={str} disabled={disabled} onChange={(e) => onSave(e.target.value)} className={cn(box, 'max-w-[320px]')}>
          <option value="">Choose</option>
          {opts.map((o) => <option key={o} value={o}>{optionLabel(field, o)}</option>)}
        </select>
      );
    }
    case 'chips': case 'countries': case 'languages': {
      const opts = field.options || [];
      const toggle = (o: string) => onSave(list.includes(o) ? list.filter((x) => x !== o) : [...list, o]);
      return (
        <div role="group" aria-label={field.label} className="flex flex-wrap gap-1.5">
          {opts.map((o) => {
            const on = list.includes(o);
            return <button key={o} type="button" disabled={disabled} aria-pressed={on} onClick={() => toggle(o)} className={cn('min-h-[32px] rounded-full border px-3 text-[13px] transition-colors disabled:opacity-60', on ? 'border-[#2e024c] bg-[#2e024c] text-white' : 'border-gray-200 bg-white text-[#344054] hover:border-[#be43ff]/40 hover:bg-[#fcf7ff]')} style={{ fontWeight: on ? 600 : 500 }}>{optionLabel(field, o)}</button>;
          })}
        </div>
      );
    }
    default:
      return (
        <>
          <TextBox id={inputId} value={str} placeholder={field.placeholder} disabled={disabled} onCommit={onSave} list={field.id === 'product_service' && products?.length ? `${inputId}-list` : undefined} type={field.kind === 'email' ? 'email' : field.kind === 'url' ? 'url' : 'text'} />
          {field.id === 'product_service' && products?.length ? <datalist id={`${inputId}-list`}>{products.map((p) => <option key={p} value={p} />)}</datalist> : null}
        </>
      );
  }
}

/** Free text, saved when the box is left or Enter is pressed (never per keystroke). */
export function TextBox({ id, value, placeholder, onCommit, area, disabled, list, type = 'text' }: { id: string; value: string; placeholder?: string; onCommit: (v: string) => void; area?: boolean; disabled?: boolean; list?: string; type?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { if (draft.trim() !== value.trim()) onCommit(draft.trim()); };
  return area
    ? <textarea id={id} rows={3} value={draft} placeholder={placeholder} disabled={disabled} onChange={(e) => setDraft(e.target.value)} onBlur={commit} className={cn(box, 'resize-y leading-relaxed')} />
    : <input id={id} type={type} list={list} value={draft} placeholder={placeholder} disabled={disabled} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }} className={box} />;
}

/** One of a few options; dark pill = chosen. */
export function Choice({ options, value, onChange, label, disabled }: { options: Array<{ id: string; label: string }>; value: string; onChange: (v: string) => void; label: string; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} disabled={disabled} onClick={() => onChange(o.id)}
          className={cn('min-h-[34px] rounded-full border px-3.5 text-[13px] transition-colors disabled:opacity-60', value === o.id ? 'border-[#2e024c] bg-[#2e024c] text-white' : 'border-gray-200 bg-white text-[#344054] hover:border-[#be43ff]/40 hover:bg-[#fcf7ff]')}
          style={{ fontWeight: value === o.id ? 600 : 500 }}>{o.label}</button>
      ))}
    </div>
  );
}
